import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import {
  GeneralDialogProps,
  ROUTES,
  useMediaQueries,
  USERS_ACTIONS
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useRefreshTableData from "../../../../../../shared/hooks/useRefreshTableData";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import InviteUserDialogContent from "./InviteUserDialogContent";

interface IInviteUserDialogProps extends GeneralDialogProps {
  tableName: string;
}

const InviteUserDialog = ({
  isOpen,
  onClose,
  tableName
}: IInviteUserDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const location = useLocation();
  const { postData } = useAxiosPrivate();
  const { handleResponse, handleCommonError } = useResponse();
  const { refreshTableData } = useRefreshTableData();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [payload, setPayload] = useState<any>(null);

  const handleCloseInviteUserDialog = () => {
    onClose(USERS_ACTIONS.InviteUser);
  };

  const resetAllValues = () => {
    setIsReady(false);
    setPayload(null);
  };

  const usersInvitationRoute = location.pathname === ROUTES.UsersInvitations;

  const handleSubmit = async () => {
    if (isReady) {
      try {
        setIsLoading(true);
        await postData(ApiResources.InviteUser, payload);

        handleResponse(ALERT_STATUS.Success, t("Dialog##invite user sent"));
        usersInvitationRoute && refreshTableData();

        handleCloseInviteUserDialog();
      } catch (error) {
        handleCommonError(error, t);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog
      data-testid={`${tableName.toLowerCase()}-invite-user-dialog`}
      title={t("Dialog##invite user")}
      description={t("Dialog##invite user description")}
      extendToMaxWidth={toMd}
      width={toMd ? undefined : DialogWidth.MinWidth}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleCloseInviteUserDialog}
      submit={handleSubmit}
      slotProps={{ transition: { onExited: () => resetAllValues() } }}
      actions={
        <DialogActions
          onClose={handleCloseInviteUserDialog}
          isLoading={isLoading}
          isreadyToConfirm={isReady}
          confirmationButtonText={t("Button##invite")}
          confirmationButtonTestId="invite-user-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-invite-user-button"
        />
      }
    >
      <InviteUserDialogContent
        setPayload={setPayload}
        setIsReady={setIsReady}
        isLoading={isLoading}
      />
    </Dialog>
  );
};

export default InviteUserDialog;
